/**
 * Image Utilities for Solomon Panel
 *
 * Provides image dimension detection, aspect ratio calculation,
 * and slot matching for profile image uploads.
 */

// ============================================================================
// Types
// ============================================================================

export interface ImageDimensions {
  width: number;
  height: number;
  ratio: number;
}

export type SlotType =
  | 'profile_vertical_image'
  | 'profile_image'
  | 'profile_horizontal_image'
  | 'profile_circle_image'
  | 'default_cover_art';

export type MatchQuality = 'exact' | 'close' | 'poor';

export interface SlotSuggestion {
  slot: SlotType;
  label: string;
  targetRatio: number;
  matchQuality: MatchQuality;
  cropRequired: boolean;
  ratioDifference: number;
}

export interface CropInfo {
  required: boolean;
  percentage: number;
  direction: 'width' | 'height' | 'none';
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Profile slot definitions with their target aspect ratios
 */
export const PROFILE_SLOTS: Record<SlotType, { ratio: number; label: string; shortLabel: string }> = {
  profile_vertical_image: {
    ratio: 9 / 16,
    label: 'Vertical Profile (9:16)',
    shortLabel: 'Vertical',
  },
  profile_image: {
    ratio: 1,
    label: 'Square Profile (1:1)',
    shortLabel: 'Square',
  },
  profile_horizontal_image: {
    ratio: 16 / 9,
    label: 'Horizontal Profile (16:9)',
    shortLabel: 'Horizontal',
  },
  profile_circle_image: {
    ratio: 1,
    label: 'Circle Avatar (1:1)',
    shortLabel: 'Circle',
  },
  default_cover_art: {
    ratio: 1,
    label: 'Default Cover Art (1:1)',
    shortLabel: 'Cover',
  },
};

/**
 * Threshold values for ratio matching
 */
const EXACT_MATCH_THRESHOLD = 0.05; // 5% difference is considered exact
const CLOSE_MATCH_THRESHOLD = 0.25; // 25% difference is considered close

// ============================================================================
// Functions
// ============================================================================

/**
 * Get image dimensions from a File object
 *
 * @param file - The image file to measure
 * @returns Promise resolving to ImageDimensions
 *
 * @example
 * const dims = await getImageDimensions(file);
 * console.log(`${dims.width}x${dims.height}, ratio: ${dims.ratio}`);
 */
export async function getImageDimensions(file: File): Promise<ImageDimensions> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
        ratio: img.naturalWidth / img.naturalHeight,
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image for dimension detection'));
    };

    img.src = URL.createObjectURL(file);
  });
}

/**
 * Calculate the percentage difference between two ratios
 */
function calculateRatioDifference(imageRatio: number, targetRatio: number): number {
  return Math.abs(imageRatio - targetRatio) / targetRatio;
}

/**
 * Determine match quality based on ratio difference
 */
function getMatchQuality(ratioDifference: number): MatchQuality {
  if (ratioDifference <= EXACT_MATCH_THRESHOLD) return 'exact';
  if (ratioDifference <= CLOSE_MATCH_THRESHOLD) return 'close';
  return 'poor';
}

/**
 * Detect the best matching slot for an image based on its aspect ratio
 *
 * @param ratio - The image's aspect ratio (width / height)
 * @returns SlotSuggestion with the best matching slot
 *
 * @example
 * const suggestion = detectBestSlot(0.5625); // 9:16 ratio
 * // Returns: { slot: 'profile_vertical_image', label: 'Vertical Profile (9:16)', ... }
 */
export function detectBestSlot(ratio: number): SlotSuggestion {
  const entries = Object.entries(PROFILE_SLOTS) as [SlotType, { ratio: number; label: string; shortLabel: string }][];

  const suggestions = entries.map(([slot, config]) => {
    const ratioDifference = calculateRatioDifference(ratio, config.ratio);
    return {
      slot,
      label: config.label,
      targetRatio: config.ratio,
      matchQuality: getMatchQuality(ratioDifference),
      cropRequired: ratioDifference > EXACT_MATCH_THRESHOLD,
      ratioDifference,
    };
  });

  // Sort by ratio difference and return best match
  suggestions.sort((a, b) => a.ratioDifference - b.ratioDifference);

  // Return best match or default to square
  return (
    suggestions[0] || {
      slot: 'profile_image',
      label: 'Square Profile (1:1)',
      targetRatio: 1,
      matchQuality: 'poor',
      cropRequired: true,
      ratioDifference: Math.abs(ratio - 1),
    }
  );
}

/**
 * Get all slot suggestions sorted by match quality
 *
 * @param ratio - The image's aspect ratio
 * @returns Array of SlotSuggestion sorted by best match first
 */
export function getAllSlotSuggestions(ratio: number): SlotSuggestion[] {
  const entries = Object.entries(PROFILE_SLOTS) as [SlotType, { ratio: number; label: string; shortLabel: string }][];

  const suggestions = entries.map(([slot, config]) => {
    const ratioDifference = calculateRatioDifference(ratio, config.ratio);
    return {
      slot,
      label: config.label,
      targetRatio: config.ratio,
      matchQuality: getMatchQuality(ratioDifference),
      cropRequired: ratioDifference > EXACT_MATCH_THRESHOLD,
      ratioDifference,
    };
  });

  // Sort by ratio difference (best match first)
  return suggestions.sort((a, b) => a.ratioDifference - b.ratioDifference);
}

/**
 * Calculate crop information for fitting an image to a target slot
 *
 * @param imageRatio - The image's aspect ratio
 * @param targetSlot - The target slot type
 * @returns CropInfo with cropping details
 */
export function calculateCropInfo(imageRatio: number, targetSlot: SlotType): CropInfo {
  const targetRatio = PROFILE_SLOTS[targetSlot].ratio;
  const ratioDifference = calculateRatioDifference(imageRatio, targetRatio);

  if (ratioDifference <= EXACT_MATCH_THRESHOLD) {
    return {
      required: false,
      percentage: 0,
      direction: 'none',
    };
  }

  // Determine which dimension needs cropping
  if (imageRatio > targetRatio) {
    // Image is wider than target - need to crop width
    const cropPercentage = ((imageRatio - targetRatio) / imageRatio) * 100;
    return {
      required: true,
      percentage: Math.round(cropPercentage),
      direction: 'width',
    };
  }
  // Image is taller than target - need to crop height
  const cropPercentage = ((targetRatio - imageRatio) / targetRatio) * 100;
  return {
    required: true,
    percentage: Math.round(cropPercentage),
    direction: 'height',
  };
}

/**
 * Format aspect ratio for display
 *
 * @param ratio - The aspect ratio (width / height)
 * @returns Formatted string like "16:9" or "4:3"
 */
export function formatRatio(ratio: number): string {
  // Common aspect ratios to match
  const commonRatios: [number, string][] = [
    [16 / 9, '16:9'],
    [9 / 16, '9:16'],
    [4 / 3, '4:3'],
    [3 / 4, '3:4'],
    [1, '1:1'],
    [3 / 2, '3:2'],
    [2 / 3, '2:3'],
    [21 / 9, '21:9'],
  ];

  // Find closest common ratio
  const match = commonRatios.find(([commonRatio]) => Math.abs(ratio - commonRatio) < 0.05);
  if (match) {
    return match[1];
  }

  // Fall back to decimal
  if (ratio > 1) {
    return `${ratio.toFixed(2)}:1`;
  }
  return `1:${(1 / ratio).toFixed(2)}`;
}

/**
 * Format file size for display
 *
 * @param bytes - File size in bytes
 * @returns Formatted string like "2.4 MB"
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
